import { useEffect, useState } from "react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { toast } from "components/ui/toast";

import {
	useWidgets,
	useCreateWidget,
	useUpdateWidget,
	useDeleteWidget,
} from "core";
import { getWidgetData, getCustomerGroupsRes } from "core";
import EditPb from "./promo-banner/EditPb";
import LeadForm from "components/leads/leads-form/LeadsForm";
import { pbConfig } from "./promo-banner/pbTemplate";
import { nlConfig } from "./newsletter/nlTemplate";
import EditNl from "./newsletter/EditNl";
import ConfirmationDialog from "components/ui/confirmationDialog";
import { SkeletonSwitch } from "skeleton";
import { WidgetGridSkeleton } from "skeleton/widgets/WidgetSkeleton";
import { PageContainer } from "../ui/page-container";
import WidgetsHeader from "./WidgetsHeader";
import { LayoutTemplate, Mail, Megaphone, Plus, ArrowRight, ChevronRight } from "lucide-react";
import { ErrorState } from "components/ui/error-state";

const WIDGET_TYPES = [
	{
		value: "NewsLetter",
		label: "Newsletter",
		icon: Mail,
		description: "Collect email subscribers with a branded signup form",
		gradient: "from-blue-500 to-indigo-600",
		lightBg: "bg-blue-50 dark:bg-blue-500/10",
		iconColor: "text-blue-600 dark:text-blue-400",
		badge: "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300",
	},
	{
		value: "PromotionBanner",
		label: "Promo Banner",
		icon: Megaphone,
		description: "Eye-catching promotional banners to drive conversions",
		gradient: "from-rose-500 to-pink-600",
		lightBg: "bg-rose-50 dark:bg-rose-500/10",
		iconColor: "text-rose-600 dark:text-rose-400",
		badge: "bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300",
	},
];

const NewsletterPreview = () => (
	<div className="w-full rounded-xl overflow-hidden border border-line-subtle dark:border-white/[0.08] bg-surface shadow-sm">
		<div className="px-5 py-4 flex flex-col items-center gap-2 bg-surface">
			<p className="font-bold text-[15px] text-[#003366]">Subscribe</p>
			<p className="text-[11px] text-content-muted text-center">Stay updated with our latest news</p>
			<div className="flex gap-2 mt-1 w-full justify-center">
				<div className="flex-1 max-w-[120px] h-7 rounded border border-line bg-gray-50 dark:bg-dark-700 text-[10px] text-content-subtle flex items-center px-2">
					Your Email
				</div>
				<div className="h-7 px-3 rounded text-white text-[10px] font-medium flex items-center" style={{ background: "#4CAF50" }}>
					Subscribe
				</div>
			</div>
		</div>
	</div>
);

const PromoBannerPreview = () => (
	<div className="w-full rounded-xl overflow-hidden border border-line-subtle dark:border-white/[0.08] shadow-sm">
		<div className="px-5 py-4 flex flex-col gap-1" style={{ background: "#d04356" }}>
			<p className="text-[11px] font-semibold uppercase text-white/80">Member Save</p>
			<p className="text-2xl font-black uppercase text-white leading-none">20% OFF</p>
			<p className="text-[11px] uppercase text-white/80">One full-price item</p>
			<div className="flex gap-2 mt-2">
				<div className="flex-1 max-w-[110px] h-7 rounded border border-white/30 bg-white/10 text-[10px] text-white/70 flex items-center px-2">
					Your Email
				</div>
				<div className="h-7 px-3 rounded text-white text-[10px] font-medium flex items-center" style={{ background: "#4CAF50" }}>
					Grab Deal
				</div>
			</div>
		</div>
	</div>
);

const EmptyState = ({ onAdd }: { onAdd: () => void }) => (
	<div className="flex flex-col items-center justify-center py-10 px-4">
		<div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-500/25 mb-5">
			<LayoutTemplate className="w-7 h-7 text-white" />
		</div>
		<h3 className="text-xl font-bold text-content dark:text-white mb-1">No widgets yet</h3>
		<p className="text-sm text-content-muted dark:text-gray-400 mb-8 text-center max-w-sm">
			Embed powerful lead-capture widgets directly into your website. Choose a type to get started.
		</p>

		{/* Widget type showcase cards */}
		<div className="grid grid-cols-1 sm:grid-cols-2 gap-5 w-full max-w-2xl mb-8">
			{WIDGET_TYPES.map((type) => {
				const Icon = type.icon;
				return (
					<button
						key={type.value}
						onClick={onAdd}
						className="group relative text-left rounded-2xl border border-line dark:border-white/[0.08] bg-surface dark:bg-dark-800 p-5 hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:shadow-lg transition-all duration-200 overflow-hidden"
					>
						<div className={`absolute top-0 right-0 w-20 h-20 rounded-bl-3xl bg-gradient-to-br ${type.gradient} opacity-10 group-hover:opacity-20 transition-opacity`} />
						<div className="flex items-center gap-3 mb-4">
							<div className={`w-10 h-10 rounded-xl flex items-center justify-center ${type.lightBg} flex-shrink-0`}>
								<Icon className={`w-5 h-5 ${type.iconColor}`} />
							</div>
							<div>
								<p className="font-semibold text-sm text-content dark:text-white">{type.label}</p>
								<p className="text-xs text-content-muted dark:text-gray-400">{type.description}</p>
							</div>
						</div>
						{type.value === "NewsLetter" ? <NewsletterPreview /> : <PromoBannerPreview />}
						<div className="flex items-center justify-between mt-3">
							<span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${type.badge}`}>
								{type.label}
							</span>
							<span className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
								Create <ChevronRight className="w-3 h-3" />
							</span>
						</div>
					</button>
				);
			})}
		</div>

		<button
			onClick={onAdd}
			className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold text-sm shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-200 active:scale-[0.98]"
		>
			<Plus className="w-4 h-4" />
			Create Your First Widget
		</button>
	</div>
);

export default function WidgetsPage() {
	const [widgets, setWidgets] = useState<any[]>([]);
	const [groupList, setGroupList] = useState<{ value: string; text: string }[]>([]);
	const [groupsLoading, setGroupsLoading] = useState(false);
	const [isProcessingWidgets, setIsProcessingWidgets] = useState(false);
	const [processedWidgetsRes, setProcessedWidgetsRes] = useState<any>(null);
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [leadGroupModalOpen, setLeadGroupModalOpen] = useState(false);
	const [newWidget, setNewWidget] = useState({
		name: "",
		type: "NewsLetter",
		groupId: "",
	});
	const [customerConfirmationOpen, setCustomerConfirmationOpen] = useState(false);
	const [selectedWidget, setSelectedWidget] = useState(null)
	const apiKey = "";

	// React Query hooks
	const {
		data: widgetsRes,
		isFetching: widgetsLoading,
		isError: widgetsError,
		error: widgetsErrorObj,
		refetch: refetchWidgets,
	} = useWidgets();
	const createWidgetMt = useCreateWidget();
	const updateWidgetMt = useUpdateWidget();
	const deleteWidgetMt = useDeleteWidget();

	// Step 3: Groups fetching
	const getGroups = async () => {
		setGroupsLoading(true);
		try {
			const res = await getCustomerGroupsRes();
			const active = (res || []).filter(
				(g: any) => g.leadsCount > 0 && g.status === "ACTIVE"
			);
			setGroupList(active.map((g: any) => ({ text: g.name, value: g.uuid })));
		} catch (err) {
			toast.error("Failed to fetch lead groups");
		} finally {
			setGroupsLoading(false);
		}
	};

	const handleTypeChange = (type: string) => {
		const typeDef = WIDGET_TYPES.find((t) => t.value === type);
		setNewWidget((prev) => ({
			...prev,
			type,
			name:
				prev.name === "" || WIDGET_TYPES.some((t) => prev.name === t.label + " Widget")
					? (typeDef?.label ?? type) + " Widget"
					: prev.name,
		}));
	};
	const openAddModal = () => {
		setNewWidget({
			name: "Newsletter Widget",
			type: "NewsLetter",
			groupId: groupList.length > 0 ? groupList[0].value : "",
		});
		setAddModalOpen(true);
	};

	useEffect(() => {
		getGroups();
	}, []);

	// Step 4c: Confirm new widget → push to local list
	const setNewWidgetHandler = () => {
		if (!newWidget.name || !newWidget.type || !newWidget.groupId) return;

		const config =
			newWidget.type.toUpperCase() === "NEWSLETTER" ? nlConfig : pbConfig;

		const newItem = {
			id: null,
			name: newWidget.name,
			type: newWidget.type,
			config,
			leadsGroup: newWidget.groupId,
			saved: false,
			widget_url: "",
		};

		setWidgets((prev) => [...prev, newItem]);
		setNewWidget({ name: "", type: "NewsLetter", groupId: "" });
		setAddModalOpen(false);
	};

	// Step 5: Save (create) — called from EditNl / EditPb
	const saveWidget = async (obj: any, index: number) => {
		try {
			await createWidgetMt.mutateAsync(obj);
			toast.success("Widget Added Successfully");
			setWidgets((prev) => prev.filter((_, i) => i !== index));
			await refetchWidgets();
		} catch (err: any) {
			console.error(err);
			toast.error("Failed to save widget");
		}
	};

	// Step 6: Update — called from EditNl / EditPb
	const handleUpdate = async (obj: any, id: string) => {
		try {
			await updateWidgetMt.mutateAsync({ body: obj, id });
			await refetchWidgets();
			toast.success("Widget Updated Successfully");
		} catch (err) {
			console.log(err);
			toast.error("Failed to update widget");
		}
	};

	// Step 7: Delete — triggered via ConfirmationDialog
	const handleDelete = async (id: string | null, _index: number) => {
		try {
			await deleteWidgetMt.mutateAsync(id);
			toast.success("Widget Deleted Successfully");
			await refetchWidgets();
			setSelectedWidget(null);
		} catch (err) {
			toast.error("Failed to delete widget");
		}
	};

	// Step 8: Local config edit — called from EditNl / EditPb on every change
	const editWidgetLocalConfig = (newConfig: any, index: number) => {
		setWidgets((prev) => {
			const copy = [...prev];
			copy[index] = { ...copy[index], config: newConfig };
			return copy;
		});
	};

	const busy =
		widgetsLoading ||
		groupsLoading ||
		isProcessingWidgets ||
		widgetsRes !== processedWidgetsRes ||
		createWidgetMt.isPending ||
		updateWidgetMt.isPending;

	const loadWidgets = async () => {
		try {
			setIsProcessingWidgets(true);
			const raw = widgetsRes?.data?.All_widgets ?? widgetsRes?.data ?? [];
			const arrayified = Array.isArray(raw) ? raw : Object.values(raw);

			const serverData = await Promise.all(
				arrayified.map(async (item: any) => {
					let config: any = {};
					if (item.config_url) {
						const cfg = await getWidgetData(item.config_url).catch(() => null);
						config = cfg?.data ?? {};
					}
					return {
						id: item.id,
						name: item.name,
						type: item.tag,
						config,
						saved: true,
						leadsGroup: null,
						widget_url: item.widget_url ?? "",
					};
				})
			);

			// Merge with any unsaved (local-only) widgets
			setWidgets((prev) => {
				const unsaved = prev.filter((w) => !w.saved);
				return [...serverData, ...unsaved];
			});
		} catch (err) {
			console.error("Failed to load widgets:", err);
		} finally {
			setProcessedWidgetsRes(widgetsRes);
			setIsProcessingWidgets(false);
		}
	};

	useEffect(() => {
		if (widgetsRes) {
			loadWidgets();
		} else if (!widgetsLoading) {
			setProcessedWidgetsRes(widgetsRes);
		}
	}, [widgetsRes, widgetsLoading]);

	const selectedTypeDef = WIDGET_TYPES.find((t) => t.value === newWidget.type);

	return (
		<PageContainer>
			<WidgetsHeader onAddWidget={openAddModal} />

			{/* Wired so Escape and an overlay click dismiss the dialog; without
			    onOpenChange, Radix cannot report either. */}
			<Dialog open={addModalOpen} onOpenChange={(next) => { if (!next) setAddModalOpen(false); }}>
				<DialogContent size="lg" padding={false} showClose={false} className="overflow-hidden" aria-labelledby="create-widget-title">
					{/* ── Gradient Header ───────────────────────── */}
					<div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-700 px-6 py-5 flex items-center gap-4">
						<div className="w-10 h-10 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
							<LayoutTemplate className="w-5 h-5 text-white" />
						</div>
						<div className="min-w-0">
							<h2 id="create-widget-title" className="text-base font-bold text-white leading-tight">Create Widget</h2>
							<p className="text-xs text-white/65 mt-0.5">Choose a type, name it, and link to a leads group</p>
						</div>
					</div>

					{/* ── Body ──────────────────────────────────── */}
					<div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">

						{/* Step 1: Widget Type */}
						<div>
							<div className="flex items-center gap-2 mb-3">
								<div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
									<span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400">1</span>
								</div>
								<p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Select Widget Type</p>
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
								{WIDGET_TYPES.map((type) => {
									const Icon = type.icon;
									const isSelected = newWidget.type === type.value;
									return (
										<button
											key={type.value}
											type="button"
											onClick={() => handleTypeChange(type.value)}
											className={`group relative text-left rounded-xl border-2 transition-all duration-200 overflow-hidden ${isSelected
												? "border-indigo-500 dark:border-indigo-400 shadow-md shadow-indigo-500/15"
												: "border-line dark:border-white/[0.08] hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:shadow-sm"
												}`}
										>
											{/* Gradient tint on selection */}
											<div className={`absolute inset-0 bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-500/10 dark:to-violet-500/5 transition-opacity duration-200 ${isSelected ? "opacity-100" : "opacity-0"}`} />

											{/* Check badge */}
											{isSelected && (
												<div className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center z-10 shadow">
													<svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none">
														<path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
													</svg>
												</div>
											)}

											{/* Card content */}
											<div className="relative z-10 flex items-start gap-3 p-4">
												<div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${type.lightBg}`}>
													<Icon className={`w-5 h-5 ${type.iconColor}`} />
												</div>
												<div className="min-w-0 flex-1">
													<p className={`font-bold text-sm leading-tight ${isSelected ? "text-indigo-700 dark:text-indigo-300" : "text-content dark:text-white"}`}>
														{type.label}
													</p>
													<p className="text-micro text-content-muted dark:text-gray-400 leading-snug mt-1">{type.description}</p>
													{isSelected && (
														<span className={`inline-flex items-center mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full ${type.badge}`}>
															Selected ✓
														</span>
													)}
												</div>
											</div>

											{/* Widget preview strip at bottom */}
											<div className="relative z-10 mx-3 mb-3 rounded-lg overflow-hidden border border-line-subtle dark:border-white/[0.06]">
												{type.value === "NewsLetter" ? <NewsletterPreview /> : <PromoBannerPreview />}
											</div>
										</button>
									);
								})}
							</div>
						</div>

						{/* Step 2: Configuration */}
						<div>
							<div className="flex items-center gap-2 mb-3">
								<div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
									<span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400">2</span>
								</div>
								<p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Configure</p>
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div>
									<label className="text-xs font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Widget Name</label>
									<Input
										value={newWidget.name}
										onChange={(e) => setNewWidget({ ...newWidget, name: e.target.value })}
										placeholder={`e.g. ${selectedTypeDef?.label ?? "My"} Widget`}
										className="dark:bg-dark-700 h-10 text-sm"
									/>
									<p className="text-[11px] text-content-subtle dark:text-gray-500 mt-1">Shown in your widget dashboard.</p>
								</div>
								<div>
									<label className="text-xs font-semibold text-content-muted dark:text-gray-400 uppercase tracking-wide mb-1.5 block">Leads Group</label>
									<Select value={newWidget.groupId} onValueChange={(v) => setNewWidget({ ...newWidget, groupId: v })}>
										<SelectTrigger className="w-full dark:bg-dark-700 h-10 text-sm">
											<SelectValue placeholder={groupsLoading ? "Loading groups…" : "Select a group"} />
										</SelectTrigger>
										<SelectContent>
											{groupList.length === 0 && !groupsLoading
												? <div className="py-3 px-3 text-sm text-content-subtle text-center">No active groups found</div>
												: groupList.map((g) => <SelectItem key={g.value} value={g.value}>{g.text}</SelectItem>)
											}
										</SelectContent>
									</Select>
									<p className="text-[11px] text-content-subtle dark:text-gray-500 mt-1">Leads captured here go to this group.</p>
								</div>
							</div>
						</div>
					</div>

					{/* ── Footer ────────────────────────────────── */}
					<div className="px-6 py-4 border-t border-line-subtle dark:border-white/[0.06] flex items-center gap-3 bg-gray-50/50 dark:bg-white/[0.02]">
						<Button
							variant="ghost"
							className="text-xs text-content-muted dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 gap-1.5 mr-auto"
							onClick={() => setLeadGroupModalOpen(true)}
						>
							<Plus className="w-3.5 h-3.5" />
							New Lead Group
						</Button>
						<Button variant="secondary" className="rounded-xl text-sm px-5" onClick={() => setAddModalOpen(false)}>
							Cancel
						</Button>
						<Button
							disabled={!newWidget.name || !newWidget.type || !newWidget.groupId}
							onClick={setNewWidgetHandler}
							className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-semibold shadow-md shadow-indigo-500/25 gap-1.5 text-sm px-5"
						>
							<ArrowRight className="w-4 h-4" />
							Create Widget
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={leadGroupModalOpen} onOpenChange={setLeadGroupModalOpen}>
				<DialogContent className="max-w-7xl">
					<DialogHeader>
						<DialogTitle className="hidden">Create Lead Group</DialogTitle>
					</DialogHeader>
					<LeadForm isModal={leadGroupModalOpen} onClose={() => setLeadGroupModalOpen(false)} onSave={() => { getGroups() }} />
				</DialogContent>
			</Dialog>

			<SkeletonSwitch isLoading={busy}
				skeleton={<WidgetGridSkeleton />}
			>
				<>
					{widgets.length > 0 ? (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start grid-flow-row-dense">
							{widgets.map((item, index) => {
								const typeDef = WIDGET_TYPES.find(
									(t) => t.value.toUpperCase() === item.type?.toUpperCase()
								);
								const TypeIcon = typeDef?.icon ?? LayoutTemplate;

								return (
									<div
										key={`${item.id ?? "temp"}-${index}`}
										className="rounded-2xl border border-line dark:border-white/[0.07] bg-surface dark:bg-dark-800 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
									>
										<div className={`flex items-center gap-2.5 px-4 py-2.5 border-b border-line-subtle dark:border-white/[0.05] ${typeDef?.lightBg ?? "bg-gray-50 dark:bg-white/[0.03]"}`}>
											<TypeIcon className={`w-4 h-4 ${typeDef?.iconColor ?? "text-content-muted"}`} />
											<span className={`text-xs font-semibold uppercase tracking-wide ${typeDef?.iconColor ?? "text-content-secondary dark:text-gray-400"}`}>
												{typeDef?.label ?? item.type}
											</span>
											{!item.saved && (
												<span className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400">
													Unsaved
												</span>
											)}
										</div>

										<div className="p-4">
											{item.type?.toUpperCase() === "NEWSLETTER" && (
												<EditNl
													widget={item}
													index={index}
													apiKey={apiKey}
													saveWidget={(obj: any, index: any) => saveWidget(obj, index)}
													editWidget={(cfg: any) => editWidgetLocalConfig(cfg, index)}
													updateWidget={(obj: any) => handleUpdate(obj, item.id)}
													deleteWidget={() => {
														setSelectedWidget(item.id);
														setCustomerConfirmationOpen(true);
													}}
												/>
											)}
											{item.type?.toUpperCase() === "PROMOTIONBANNER" && (
												<EditPb
													widget={item}
													index={index}
													apiKey={apiKey}
													saveWidget={(obj: any, index: any) => saveWidget(obj, index)}
													editWidget={(cfg: any) => editWidgetLocalConfig(cfg, index)}
													updateWidget={(obj: any) => handleUpdate(obj, item.id)}
													deleteWidget={() => {
														setSelectedWidget(item.id);
														setCustomerConfirmationOpen(true);
													}}
												/>
											)}
										</div>
									</div>
								);
							})}
						</div>
					) : widgetsError ? (
						/* Without this a failed request rendered the empty state,
						   telling the user they had no widgets when the server
						   had simply not answered. */
						<ErrorState
							title="Couldn't load your widgets"
							description="The request didn't complete. Your widgets are safe — this is only the view."
							error={widgetsErrorObj}
							onRetry={() => refetchWidgets()}
						/>
					) : (
						<EmptyState onAdd={openAddModal} />
					)}
				</>
			</SkeletonSwitch>

			<ConfirmationDialog
				open={customerConfirmationOpen}
				title="Delete this Widget?"
				description="This action cannot be undone."
				confirmLabel="Delete"
				cancelLabel="Cancel"
				variant="destructive"
				onConfirm={async () => {
					handleDelete(selectedWidget, 0)
					setCustomerConfirmationOpen(false);
				}}
				onCancel={() => setCustomerConfirmationOpen(false)}
			/>

		</PageContainer>
	);
}
